import { useCallback } from 'react';

import type { IWeek, IWeekDay } from '@/types/calendar';
import type { ISortedGenericShift, ISortedShift, ScheduleId } from '@/types/schedule';

import { AllShiftItemType } from '@/store/enums';
import { useTimezone, useWeek } from '@/hooks';
import { checkCollision, getDisplayBlocksForMultiDayView } from '@/helpers';
import { CachedBlock } from '@/classes/cachedBlock';
import { getBlockDayOffset } from '@/helpers/block';

interface IGenericShift {
  startBlock: number;
  endBlock: number;
  shiftId: number;
  scheduleId: ScheduleId;
}

type ISplitGenericShift = IGenericShift & {
  originalStartBlock: number;
  originalEndBlock: number;
};

type ISplitShift<T extends IGenericShift> = (
  item: T,
  timezoneName: string,
) => (T & ISplitGenericShift) | [T & ISplitGenericShift, T & ISplitGenericShift];
type IMapToSorted<T extends IGenericShift> = (
  item: T & ISplitGenericShift,
  itemType: AllShiftItemType,
) => T & ISortedGenericShift;
type IFilterShifts<T extends IGenericShift> = (item: T) => boolean;

type IParseShifts<T extends IGenericShift> = (item: T[][]) => (T & ISortedGenericShift)[];
type IGetRowTopPosition<T extends ISortedShift> = (item: (T | ISortedGenericShift)[][]) => number;
type IRemoveEmptyColsFromRow<T extends IGenericShift & ISortedGenericShift> = (item: T[][]) => T[][];
type IGetColSpan<T extends IGenericShift & ISortedGenericShift> = (
  shift: T,
  columnIndex: number,
  newRow: T[][],
) => number;
type ICheckCollision<T extends IGenericShift & ISortedGenericShift> = (shift1: T, shift2: T) => boolean;

interface IProps {
  day: IWeekDay;
  week: IWeek;
}

interface IReturn<T extends IGenericShift> {
  splitShift: ISplitShift<T>;
  mapToSorted: IMapToSorted<T>;
  filterShifts: IFilterShifts<T & ISortedGenericShift>;
  parseShifts: IParseShifts<T>;
  getRowTopPosition: IGetRowTopPosition<ISortedShift>;
  removeEmptyColsFromRow: IRemoveEmptyColsFromRow<T & ISortedGenericShift>;
  getColSpan: IGetColSpan<T & ISortedGenericShift>;
}

function useCalendarShifts<T extends IGenericShift>({ day, week }: IProps): IReturn<T> {
  const { timezoneName } = useTimezone();
  const { selectedDays } = useWeek();

  const splitShift = useCallback<ISplitShift<T>>((item, timezoneName) => {
    const isSplitDay =
      CachedBlock.fromBlockId(item.startBlock, timezoneName).day !==
      CachedBlock.fromBlockId(item.endBlock, timezoneName).day;
    const itemToReturn = { ...item, originalStartBlock: item.startBlock, originalEndBlock: item.endBlock };

    if (isSplitDay) {
      const nextItemEndBlock = CachedBlock.fromBlockId(item.endBlock, timezoneName);
      const midnightBlock = nextItemEndBlock.startOfDay();

      const prevItem = {
        ...item,
        originalStartBlock: item.startBlock,
        originalEndBlock: item.endBlock,
        endBlock: midnightBlock.blockId - 1,
        splitStart: true,
      };
      const nextItem = {
        ...item,
        originalStartBlock: item.startBlock,
        originalEndBlock: item.endBlock,
        startBlock: midnightBlock.blockId,
        splitEnd: true,
      };
      return [prevItem, nextItem];
    }

    return itemToReturn;
  }, []);

  const mapToSorted = useCallback<IMapToSorted<T>>(
    (item, itemType) => {
      const blockData = CachedBlock.fromBlockId(item.startBlock, timezoneName);
      const displayBlocks = getDisplayBlocksForMultiDayView(week, timezoneName, item, selectedDays, day);
      return {
        ...item,
        dayNumber: blockData.day,
        monthNumber: blockData.month,
        itemData: {
          startBlock: item.startBlock,
          endBlock: item.endBlock,
          itemType,
          id: `shift-${itemType}-${item.shiftId}`,
        },
        displayBlocks,
      };
    },
    [day, selectedDays, timezoneName, week],
  );

  const filterShifts: IFilterShifts<T & ISortedGenericShift> = useCallback(
    item => item.dayNumber === day.dayNumber && item.monthNumber === day.monthNumber,
    [day.dayNumber, day.monthNumber],
  );

  const parseShifts: IReturn<T>['parseShifts'] = useCallback(
    items =>
      items
        .flat()
        .flatMap(item => splitShift(item, timezoneName))
        .map(mapToSorted)
        .filter(filterShifts)
        .sort((a, b) => a.displayBlocks.startBlock - b.displayBlocks.startBlock),
    [filterShifts, mapToSorted, splitShift, timezoneName],
  );

  const getRowTopPosition = useCallback<IReturn<T>['getRowTopPosition']>(
    row => {
      const flatRow = row.flat().sort((a, b) => a.displayBlocks.startBlock - b.displayBlocks.startBlock);
      return getBlockDayOffset(flatRow[0].displayBlocks.startBlock, timezoneName, day) ?? 0;
    },
    [day, timezoneName],
  );

  const _checkCollision: ICheckCollision<T & ISortedGenericShift> = (shift1, shift2) =>
    checkCollision(shift1.displayBlocks, shift2.displayBlocks);

  const getColSpan = useCallback<IReturn<T>['getColSpan']>((shift, columnIndex, newRow): number => {
    let colSpan = 1;
    if (columnIndex === newRow.length - 1) {
      return colSpan;
    }

    for (let x = columnIndex + 1, len = newRow.length; x < len; x++) {
      const nextColumn = newRow[columnIndex + colSpan];
      if (!nextColumn || nextColumn.some(item => _checkCollision(shift, item))) {
        break;
      }

      colSpan++;
    }

    return colSpan;
  }, []);

  const removeEmptyColsFromRow = useCallback<IReturn<T>['removeEmptyColsFromRow']>(
    row => row.filter(column => Boolean(column.length)),
    [],
  );

  return {
    splitShift,
    mapToSorted,
    filterShifts,
    parseShifts,
    getRowTopPosition,
    removeEmptyColsFromRow,
    getColSpan,
  };
}

export { useCalendarShifts };
